import React from 'react';

import { MtxIcon } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.css';

import type { QpsAnswer } from '@/types/qps';

// Libellés envoyés par l'API en capitales : affichés « Vrai » / « Faux » comme sur la maquette.
const ANSWER_LABEL_KEYS: Record<string, string> = {
  VRAI: 'qps.answers.true',
  FAUX: 'qps.answers.false',
};

interface QpsAnswerChoiceProps {
  answer: QpsAnswer;
  isSelected: boolean;
  isLocked: boolean;
  isFocusable: boolean;
  showResult: boolean;
  onSelect: (value: string) => void;
}

const QpsAnswerChoice: React.FC<QpsAnswerChoiceProps> = ({
  answer,
  isSelected,
  isLocked,
  isFocusable,
  showResult,
  onSelect,
}) => {
  const { t } = useTranslation();
  const labelKey = ANSWER_LABEL_KEYS[answer.label.trim().toUpperCase()];
  // Seule la réponse choisie porte un état juste/faux : la maquette 19-9103 laisse la bonne
  // réponse non choisie en état par défaut.
  const isResultShown = isSelected && showResult;
  const classNames = [styles.choice];
  if (isResultShown) classNames.push(answer.isCorrect ? styles.correct : styles.incorrect);

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      aria-disabled={isLocked}
      tabIndex={isFocusable ? 0 : -1}
      className={classNames.join(' ')}
      onClick={() => {
        if (!isLocked) onSelect(answer.value);
      }}
    >
      {/* L'icône est une police de glyphes rendue en shadow DOM sans ARIA : sans ce wrapper son
          `::before` entre dans le nom accessible du `role="radio"`. */}
      {isResultShown && (
        <span aria-hidden="true">
          {/* Pleine si la réponse est juste, en contour si elle est fausse (maquettes 16-8870 et 19-9119). */}
          <MtxIcon name={answer.isCorrect ? 'check-circle-solid' : 'cross-circle'} size={24} />
        </span>
      )}
      <span className={styles.label}>{labelKey ? t(labelKey) : answer.label}</span>
      {isResultShown && <span className={styles.iconSpacer} aria-hidden="true" />}
    </button>
  );
};

export default QpsAnswerChoice;
